import { Button } from '@systemeio/components';
import { SlashCircle01Icon, Trash02Icon } from '@systemeio/icons';
import { BadRequest } from '@systemeio/util';
import { OfferConfigurationDisclosure } from 'modules/funnels/funnel/components/step-configuration-tab/offer-configuration-disclosure';
import { useSaleLimit } from 'modules/funnels/funnel/components/step-configuration-tab/sale-limit/hooks/use-sale-limit';
import { SaleLimitEditInterface, SaleLimitViewInterface } from 'modules/funnels/funnel/components/step-configuration-tab/sale-limit/types/sale-limit-interface';
import { FunnelStepId } from 'modules/funnels/funnel/types/funnel-step-interface';
import { ListingDataInterface } from 'modules/funnels/funnel/utils/offer-configuration-utils';
import { useEffect, useState } from 'react';
import { FormInputNumberGroup, FormSelect, Hint } from 'shared/components';
import ConfirmationModal from 'shared/components/confirmation-modal';
import { changedFromFetched, useActOnBlur } from 'shared/hooks/use-act-on-blur';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
interface SaleLimitProps extends SaleLimitViewInterface {
  saleLimitFunnelStepData: ListingDataInterface[];
  funnelStepId: FunnelStepId;
  onSave: (data: SaleLimitEditInterface) => Promise<void>;
}
const defaultValues: SaleLimitEditInterface = {
  saleLimit: null,
  saleLimitFunnelStepRedirect: null
};
function SaleLimit({
  saleLimit,
  saleLimitFunnelStepRedirect,
  saleLimitFunnelStepData,
  funnelStepId,
  onSave
}: SaleLimitProps) {
  const {
    t
  } = useLocoTranslation();
  const [tempState, setTempState] = useState(defaultValues);
  const [isOpen, setIsOpen] = useState(false);
  const {
    saleLimitData
  } = useSaleLimit(funnelStepId, !!saleLimit);
  const [modalOpened, setModalOpened] = useState(false);
  const handleError = (error: Error) => {
    if (error instanceof BadRequest) {
      const errors = error.errors.fields;
      salesLimitActor.setStatus(errors.saleLimit?.join(', '));
      saleLimitFunnelStepRedirectActor.setStatus(errors.saleLimitRedirectStep?.join(', ') || errors.saleLimitFunnelStepRedirect?.join(', '));
    }
  };
  const salesLimitActor = useActOnBlur<SaleLimitEditInterface['saleLimit']>(() => ({
    action: async value => {
      await onSave({
        ...tempState,
        saleLimit: value
      });
    },
    // An unset limit shows as 0, so 0 is what an untouched field blurs with.
    isValueChanged: changedFromFetched<SaleLimitEditInterface['saleLimit']>(saleLimit ?? 0),
    onError: handleError
  }));

  // `null`, never `undefined`: the select clears to null, and the comparator reads `undefined` as
  // "nothing saved in this session yet".
  const saleLimitFunnelStepRedirectActor = useActOnBlur<FunnelStepId | null>(() => ({
    action: async value_0 => {
      await onSave({
        ...tempState,
        saleLimitFunnelStepRedirect: value_0
      });
    },
    isValueChanged: changedFromFetched(saleLimitFunnelStepRedirect?.id ?? null),
    onError: handleError
  }));
  useEffect(() => {
    if (saleLimit || saleLimitFunnelStepRedirect) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    setTempState({
      saleLimit,
      saleLimitFunnelStepRedirect: saleLimitFunnelStepRedirect?.id
    });
  }, [saleLimit, saleLimitFunnelStepRedirect]);
  const onChangeSaleLimitFunnelStep = async (data?: number) => {
    const funnelStep = saleLimitFunnelStepData.find(item => item.id === data);
    const newData = {
      ...tempState,
      saleLimitFunnelStepRedirect: funnelStep?.id || null
    };
    setTempState(newData);
    await saleLimitFunnelStepRedirectActor.handleBlur(newData.saleLimitFunnelStepRedirect);
  };
  const hasSavedData = saleLimit !== null || saleLimitFunnelStepRedirect !== null;
  const resetState = () => {
    setIsOpen(false);
    setTempState(defaultValues);
    // Drop what the actors believe is saved, or the next blur compares against a removed limit.
    salesLimitActor.setActualValue(undefined);
    saleLimitFunnelStepRedirectActor.setActualValue(undefined);
  };
  const onRemoveClick = () => {
    // Nothing is persisted yet, so there is nothing to remove on the back end.
    if (!hasSavedData) {
      resetState();
      return;
    }
    setModalOpened(true);
  };
  const onRemove = async () => {
    await onSave(defaultValues).catch(handleError);
    resetState();
  };
  return <>
      <OfferConfigurationDisclosure icon={<SlashCircle01Icon />} title={<Hint inline passive label={t('dashboard.funnel.configuration.offer.sales_limit_popover')}>
            {t('dashboard.funnel.configuration.offer.sales_limit')}
          </Hint>} description={t('dashboard.funnel.configuration.offer.sales_limit-description')} onOpenChange={setIsOpen} open={isOpen}>
        <div className="tablet:flex-row flex flex-col items-end justify-between gap-3">
          <FormInputNumberGroup type="integer" label={<Hint showHint={!!saleLimitData} label={saleLimitData ? t('dashboard.funnel.configuration.offer.sales_limit_sold', {
          salesCount: saleLimitData.salesCount,
          salesLimit: saleLimit
        }) : undefined}>
                {t('dashboard.funnel.configuration.offer.sales_limit')}
              </Hint>} required value={tempState.saleLimit === null ? 0 : tempState.saleLimit} onChange={value_1 => setTempState(prev => ({
          ...prev,
          saleLimit: value_1
        }))} onBlur={(_, processedNumber) => salesLimitActor.handleBlur(processedNumber)} maxLength={256} statusIcon={salesLimitActor.statusIcon} error={salesLimitActor.error} />
          <FormSelect label={t('dashboard.funnel.configuration.offer.redirect_funnel_step')} value={tempState.saleLimitFunnelStepRedirect || undefined} data={saleLimitFunnelStepData} onChange={onChangeSaleLimitFunnelStep} required statusIcon={saleLimitFunnelStepRedirectActor.statusIcon} error={saleLimitFunnelStepRedirectActor.error} />
          <Button variant="gray" size="icon-md" aria-label={t('global.delete')} onClick={onRemoveClick}>
            <Trash02Icon />
          </Button>
        </div>
      </OfferConfigurationDisclosure>
      <ConfirmationModal status="error" confirmationText={t('dashboard.funnel.configuration.offer.remove_offer_sale_limit')} opened={modalOpened} onClose={() => setModalOpened(false)} onConfirm={onRemove} />
    </>;
}
export default SaleLimit;